/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  active: {
    bg: 'color-mix(in srgb, var(--status-success) 28%, var(--surface-primary))',
    color: 'var(--status-success-text)',
  },
  inactive: {
    bg: 'color-mix(in srgb, var(--status-error) 28%, var(--surface-primary))',
    color: 'var(--status-error-text)',
  },
  featured: {
    bg: 'color-mix(in srgb, var(--status-warning) 28%, var(--surface-primary))',
    color: 'var(--status-warning-text)',
  },
};

export interface StatusBadgeProps {
  status: 'active' | 'inactive' | 'featured';
  /**
   * Chip sized to sit inline beside a 18px title line instead of owning a
   * column of its own. Same colors — only the box shrinks.
   */
  compact?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, compact }) => {
  const { t } = useTranslation('features/properties');
  const styles = STATUS_STYLES[status] || STATUS_STYLES.active;

  const sizing: React.CSSProperties = compact
    ? {
        flexShrink: 0,
        height: 18,
        padding: '0 var(--space-2)',
        fontSize: 'var(--text-tag)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        borderRadius: 'var(--radius-sm)',
      }
    : {
        padding: 'var(--space-1) var(--space-2)',
        fontSize: 'var(--text-meta)',
        borderRadius: 'var(--radius-md)',
      };

  return (
    <Text
      as="span"
      data-testid="status-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontWeight: 'var(--font-semibold)' as unknown as number,
        background: styles.bg,
        color: styles.color,
        fontFamily: 'var(--font-family)',
        ...sizing,
      }}
    >
      {t(`status.${status}`)}
    </Text>
  );
};
